import React from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  styled,
  Popover,
} from '@mui/material';
import { heroStyles as styles } from '../../assets/styles/home';
import { useNavigate } from 'react-router-dom';
import backgroundTriangle from '../../assets/img/bgn-triangle-bottom.webp';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper';
import { useTranslation } from 'react-i18next';
import webBg2 from '../../assets/img/web-bg-5.png';
import webBgAa from '../../assets/img/web-bg-2.webp';
import man from '../../assets/img/man.webp';
import office1 from '../../assets/img/office3.webp';

import 'swiper/css';
import 'swiper/css/effect-fade';
import Newsletter from './Newsletter';

type SliderContainerProps = {
  bgImg: string;
};

const SliderContainer = styled('div')<SliderContainerProps>(({ bgImg }) => ({
  maxWidth: '100%',
  minHeight: '70vh',
  background: `url(${bgImg}) center center no-repeat`,
  backgroundSize: 'cover',
}));

const Hero = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'newsletter' : undefined;
  const handleLink = (link: string) => {
    navigate(link);
  };
  return (
    <Box sx={{ position: 'relative' }}>
      <Swiper
        spaceBetween={30}
        centeredSlides={true}
        effect={'fade'}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        navigation={true}
        modules={[Autoplay, EffectFade]}
        className="mySwiper"
      >
        {intros.map((intro) => (
          <SwiperSlide key={intro.id}>
            <SliderContainer bgImg={intro.bg}>
              <Container maxWidth="lg" sx={styles.container}>
                <Box component="div" sx={styles.bodyFlex}>
                  <Box component="div" sx={[styles.heroText, styles.header]}>
                    <Typography
                      variant="h2"
                      sx={{
                        ...styles.flex,
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        fontWeight: 'bold',
                      }}
                    >
                      <Box component="span" sx={styles.line} />
                      {t(intro.title)}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        ...styles.lightText,
                        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.6)',
                      }}
                    >
                      {t(intro.lightText)}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        ...styles.boldText,
                        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.6)',
                      }}
                    >
                      {t(intro.boldText)}
                    </Typography>
                    <Box component="div" sx={styles.buttonCon}>
                      <Button
                        id={`${intro.id}-${intro.button1.link}`}
                        onClick={() => {
                          // Check if it's an external URL
                          if (
                            intro.button1.link.startsWith('http://') ||
                            intro.button1.link.startsWith('https://')
                          ) {
                            window.open(intro.button1.link, '_blank');
                          } else {
                            navigate(`${intro.button1.link}`);
                          }
                        }}
                        variant="outlined"
                        sx={styles.button1}
                      >
                        {t(intro.button1.text)}
                      </Button>
                      {intro.button2.link === '/newuser' ? (
                        <Button
                          onClick={handleClick}
                          variant="contained"
                          sx={styles.button2}
                        >
                          {t(intro.button2.text)}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            if (
                              intro.button2.link.startsWith('http://') ||
                              intro.button2.link.startsWith('https://')
                            ) {
                              window.open(intro.button2.link, '_blank');
                            } else {
                              handleLink(`${intro.button2.link}`);
                            }
                          }}
                          variant="contained"
                          sx={styles.button2}
                        >
                          {t(intro.button2.text)}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Container>
            </SliderContainer>
          </SwiperSlide>
        ))}
      </Swiper>

      <img
        src={backgroundTriangle}
        alt="background triangle"
        style={styles.img as React.CSSProperties | undefined}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Newsletter />
      </Popover>
    </Box>
  );
};

export default Hero;

const intros = [
  {
    id: 1,
    title: 'hero.intros.intro1.title',
    lightText: 'hero.intros.intro1.lightText',
    boldText: 'hero.intros.intro1.boldText',
    button1: { text: 'hero.intros.intro1.button1', link: '/contact' },
    button2: { text: 'hero.intros.intro1.button2', link: '/newuser' },
    bg: office1,
  },
  {
    id: 2,
    title: 'hero.intros.intro2.title',
    lightText: 'hero.intros.intro2.lightText',
    boldText: 'hero.intros.intro2.boldText',
    button1: { text: 'hero.intros.intro2.button1', link: 'services' },
    button2: { text: 'hero.intros.intro2.button2', link: 'appointment' },
    bg: webBg2,
  },
  {
    id: 3,
    title: 'hero.intros.intro3.title',
    lightText: 'hero.intros.intro3.lightText',
    boldText: 'hero.intros.intro3.boldText',
    button1: {
      text: 'hero.intros.intro3.button1',
      link: 'https://aefinancialservicesllc.taxdome.com/en-gb/signup',
    },
    button2: { text: 'hero.intros.intro3.button2', link: 'services' },
    bg: man,
  },
  {
    id: 4,
    title: 'hero.intros.intro4.title',
    lightText: 'hero.intros.intro4.lightText',
    boldText: 'hero.intros.intro4.boldText',
    button1: { text: 'hero.intros.intro4.button1', link: 'newuser' },
    button2: { text: 'hero.intros.intro4.button2', link: 'contact' },
    bg: webBgAa,
  },
];
